import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "@apa/core-renderers/styles.css";
import {
	ComponentTypes,
	type All8PageComponent,
	type All8PageContent,
	type LatestArticleGridContent,
	type MarketsBoardContent,
	type PaginatedActivityGridContent,
	type HomeNewsContent
} from "@apa/core-renderers";
import { client } from "../Clients";
import type { ContentDocument, ContentTypeConfig, ContentViewModel } from "../API/Models";
import { usePageHeader } from "../hooks/usePageHeader";
import { createRuntimeImageComponent } from "../Helper/storage";
import { reorderList } from "../Components/LayoutEditor/utils";
import { showSuccessModal } from "../Helper/showSuccessModal";
import PageEditorHeaderActions from "../Components/PageEditor/PageEditorHeaderActions";
import AIAnalyzeModal from "../Components/AIAnalyzeModal";
import PageEditorLayout, { type ContentViewMode } from "../Components/PageEditor/PageEditorLayout";
import {
	PREVIEW_MAX_WIDTH,
	PREVIEW_VIEWPORTS,
	type PreviewViewportKey
} from "../Components/PageEditor/PageEditorPreviewPanel";
import type { AdminPage, PageMetadata } from "../Components/PageEditor/types";
import {
	attachComponents,
	buildDraftFromContentDoc,
	inputToKeywords,
	keywordsToInput,
	normalizeComponents,
	parsePagePayload,
	previewFingerprint,
	safeDecodeSlug,
	serializePagePayload,
	setValueAtPath
} from "../Components/PageEditor/pageEditorUtils";
import { pageToEditableMarkdown } from "../Components/PageEditor/pageToEditableMarkdown";
import { editableMarkdownToPage } from "../Components/PageEditor/editableMarkdownToPage";
import {
	componentLibrary,
	componentDefinitionMap,
	createComponentInstance,
	type ComponentDefinition,
	type Path
} from "./pageComponentLibrary";
import type { IApplicationState } from "../Redux/Reducers";
import { useVariableFormatting } from "../Components/PageEditor/useVariableFormatting";
import { useBreadcrumbs } from "../Components/PageEditor/useBreadcrumbs";
import { useDynamicContentResolvers } from "../Components/PageEditor/useDynamicContentResolvers";
import { loadBlockFragments, overlayBlockFragments, type BlockFragmentMap } from "../Components/PageEditor/blockFragmentPreview";
import { usePagePersistence } from "../Components/PageEditor/usePagePersistence";
import PageEditorModals from "../Components/PageEditor/PageEditorModals";
import { EditLockBanner } from "../Realtime/useEditLock";

type RouteParams = {
	lang?: string;
	slug?: string;
	id?: string;
};

const DEFAULT_LANGUAGE = "fr";
const DEFAULT_DOMAIN = "https://www.apa.cd";
const PAGE_SCHEMA_KEY = "page";

type DeploymentState = "up-to-date" | "needs-deploy" | "pending";

/** Component-based page editor with real-time preview, deployment and AI analysis. */
const PageEditor: React.FC = () => {
	const params = useParams<RouteParams>();
	const lang = params.lang;
	const routeSlug = params.slug;
	const routeId = params.id;

	const slug = useMemo(() => {
		if (!routeSlug) return undefined;
		const decoded = safeDecodeSlug(routeSlug);
		return decoded ?? routeSlug;
	}, [routeSlug]);

	const id = useMemo(() => {
		if (!routeId) return undefined;
		const decoded = safeDecodeSlug(routeId);
		return decoded ?? routeId;
	}, [routeId]);
	const history = useHistory();
	const location = useLocation();
	const isNewDocument = location.pathname === "/new-page";
	const [reloadCount, setReloadCount] = useState(0);
	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const prefix = searchParams.get("prefix") ?? "";
	const slugFromQuery = searchParams.get("slug") ?? "";
	const initialLanguage = (searchParams.get("lang") ?? lang ?? DEFAULT_LANGUAGE).toLowerCase();

	const [draft, setDraft] = useState<AdminPage | null>(null);
	const [showPageAnalyze, setShowPageAnalyze] = useState(false);
	const [showSlugChange, setShowSlugChange] = useState(false);
	const [slugChangeValue, setSlugChangeValue] = useState("");
	const [slugChangeError, setSlugChangeError] = useState<string | null>(null);
	const [changingSlug, setChangingSlug] = useState(false);
	const [connectionsOpen, setConnectionsOpen] = useState(false);
	const [showLibrary, setShowLibrary] = useState(false);
	const [libraryView, setLibraryView] = useState<"grid" | "list" | "visual">("grid");
	const [librarySelection, setLibrarySelection] = useState<ComponentDefinition | null>(() => componentLibrary[0] ?? null);
	const [metaOpen, setMetaOpen] = useState(false);
	const [componentSearch, setComponentSearch] = useState("");
	const [componentTagFilters, setComponentTagFilters] = useState<string[]>([]);
	const [activeComponentKey, setActiveComponentKey] = useState<string | null>(null);
	const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>({});
	const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});
	const [contentViewMode, setContentViewMode] = useState<ContentViewMode>("blocks");
	const [pageJsonDraft, setPageJsonDraft] = useState<string>("");
	const [pageJsonError, setPageJsonError] = useState<string | null>(null);
	const [markdownDraft, setMarkdownDraft] = useState<string>("");
	const [markdownError, setMarkdownError] = useState<string | null>(null);
	const lastDraftFromMarkdownRef = useRef<AdminPage | null>(null);
	const lastMarkdownPageKeyRef = useRef<string>("");
	const markdownPreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const markdownDraftRef = useRef<string>("");
	const draftRef = useRef<AdminPage | null>(draft);
	draftRef.current = draft;
	const MARKDOWN_PREVIEW_DEBOUNCE_MS = 800;
	const [previewViewport, setPreviewViewport] = useState<PreviewViewportKey>("full");
	const [previewContainerWidth, setPreviewContainerWidth] = useState<number | null>(null);
	const previewContainerRef = useRef<HTMLDivElement | null>(null);
	const [showDynamicPreview, setShowDynamicPreview] = useState(false);
	const [dynamicPreviewItems, setDynamicPreviewItems] = useState<ContentDocument[]>([]);
	const [dynamicPreviewLoading, setDynamicPreviewLoading] = useState(false);
	const [dynamicPreviewError, setDynamicPreviewError] = useState<string | null>(null);
	const [dynamicPreviewSearch, setDynamicPreviewSearch] = useState("");
	const [dynamicPreviewSelection, setDynamicPreviewSelection] = useState<ContentDocument | null>(null);
	const [newsSectionItemsByType, setNewsSectionItemsByType] = useState<Record<string, ContentDocument[]>>({});
	const [newsSectionErrors, setNewsSectionErrors] = useState<Record<string, string>>({});
	const [newsSectionLoading, setNewsSectionLoading] = useState<Record<string, boolean>>({});
	const [newsSectionDynamicPages, setNewsSectionDynamicPages] = useState<Record<string, ContentDocument>>({});
	const [paginatedActivityItemsByType, setPaginatedActivityItemsByType] = useState<Record<string, ContentDocument[]>>({});
	const [paginatedActivityErrors, setPaginatedActivityErrors] = useState<Record<string, string>>({});
	const [paginatedActivityLoading, setPaginatedActivityLoading] = useState<Record<string, boolean>>({});
	const [latestArticleItemsByType, setLatestArticleItemsByType] = useState<Record<string, ContentDocument[]>>({});
	const [marketsBoardInflationItemsByType, setMarketsBoardInflationItemsByType] = useState<Record<string, ContentDocument[]>>({});
	// Baked block-fragments (preview env) so the page preview renders the home dashboard widgets —
	// MarketsBoard cards, FxRatesExplorer, InformationHub, governors — from the SAME CDN files the live
	// site reads, matching deployment. This overlay is the source of truth for computed widgets in the
	// preview; it is applied LAST in `previewComponents` so it supersedes any per-widget projection.
	const [blockFragments, setBlockFragments] = useState<BlockFragmentMap>({});
	const [deploymentQueuePreview, setDeploymentQueuePreview] = useState<import("../API/Models").DeploymentQueueItem[]>([]);
	const [deploymentQueueProduction, setDeploymentQueueProduction] = useState<import("../API/Models").DeploymentQueueItem[]>([]);
	const [deploymentQueueLoading, setDeploymentQueueLoading] = useState(false);
	const [deploymentQueueRefresh, setDeploymentQueueRefresh] = useState(0);
	const viewModeRef = useRef<ContentViewMode>(contentViewMode);
	const lastJsonPageKeyRef = useRef<string>("");
	const [pageNavOpen, setPageNavOpen] = useState(false);

	const setDraftWithComponents = useCallback((next: AdminPage | null) => setDraft(attachComponents(next)), []);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const categories = useSelector((state: IApplicationState) => state.categories.items);
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);
	const categoryKeyMap = useMemo(() => {
		const map = new Map<string, string>();
		categories.forEach((category) => {
			if (category.id) {
				map.set(category.id, category.key ?? category.title ?? category.id);
			}
		});
		return map;
	}, [categories]);
	const categoryTitleMap = useMemo(() => {
		const map = new Map<string, string>();
		categories.forEach((category) => {
			if (category.id) {
				map.set(category.id, category.title ?? category.key ?? category.id);
			}
		});
		return map;
	}, [categories]);
	const dynamicContentTypeConfig = useMemo<ContentTypeConfig | null>(
		() => contentTypeConfigs.find((config) => config.id === draft?.dynamicContentTypeId) ?? null,
		[contentTypeConfigs, draft?.dynamicContentTypeId]
	);
	const basePreviewComponents = useMemo<All8PageContent>(() => normalizeComponents(draft?.components), [draft?.components]);
	const newsSectionContentTypes = useMemo(() => {
		const keys = new Set<string>();
		basePreviewComponents.forEach((component) => {
			if (component.componentType !== ComponentTypes.NewsSection) return;
			const content = (component.content ?? {}) as HomeNewsContent;
			if (content.mode !== "dynamic") return;
			const key = (content.dynamicComponent?.contentTypeKey ?? "").toString().trim();
			if (key) keys.add(key);
		});
		return Array.from(keys);
	}, [basePreviewComponents]);
	const paginatedActivityContentTypes = useMemo(() => {
		const keys = new Set<string>();
		basePreviewComponents.forEach((component) => {
			if (component.componentType !== ComponentTypes.PaginatedActivityGrid) return;
			const content = (component.content ?? {}) as PaginatedActivityGridContent;
			if (content.mode !== "dynamic") return;
			const key = (content.dynamicComponent?.contentTypeKey ?? "").toString().trim();
			if (key) keys.add(key);
		});
		return Array.from(keys);
	}, [basePreviewComponents]);
	const latestArticleContentTypes = useMemo(() => {
		const keys = new Set<string>();
		basePreviewComponents.forEach((component) => {
			if (component.componentType !== ComponentTypes.LatestArticleGrid) return;
			const content = (component.content ?? {}) as LatestArticleGridContent;
			if (content.mode !== "dynamic") return;
			const key = (content.dynamicComponent?.contentTypeKey ?? "").toString().trim();
			if (key) keys.add(key);
		});
		return Array.from(keys);
	}, [basePreviewComponents]);
	// Same shape as the others: collect every content type referenced by a
	// MarketsBoard component's inflation block in dynamic mode. The loader
	// effect below fetches one batch per key so the preview can resolve them.
	const marketsBoardInflationContentTypes = useMemo(() => {
		const keys = new Set<string>();
		basePreviewComponents.forEach((component) => {
			if (component.componentType !== ComponentTypes.MarketsBoard) return;
			const content = (component.content ?? {}) as MarketsBoardContent;
			const inflation = content.inflation;
			if (!inflation || inflation.mode !== "dynamic") return;
			const key = (inflation.dynamicComponent?.contentTypeKey ?? "").toString().trim();
			if (key) keys.add(key);
		});
		return Array.from(keys);
	}, [basePreviewComponents]);
	const newsSectionDynamicPageIds = useMemo(() => {
		const ids = new Set<string>();
		basePreviewComponents.forEach((component) => {
			if (component.componentType !== ComponentTypes.NewsSection) return;
			const content = (component.content ?? {}) as HomeNewsContent;
			if (content.mode !== "dynamic") return;
			const id = (content.dynamicComponent?.dynamicPageId ?? "").toString().trim();
			if (id) ids.add(id);
		});
		return Array.from(ids);
	}, [basePreviewComponents]);
	const paginatedActivityDynamicPageIds = useMemo(() => {
		const ids = new Set<string>();
		basePreviewComponents.forEach((component) => {
			if (component.componentType !== ComponentTypes.PaginatedActivityGrid) return;
			const content = (component.content ?? {}) as PaginatedActivityGridContent;
			if (content.mode !== "dynamic") return;
			const id = (content.dynamicComponent?.dynamicPageId ?? "").toString().trim();
			if (id) ids.add(id);
		});
		return Array.from(ids);
	}, [basePreviewComponents]);
	const latestArticleDynamicPageIds = useMemo(() => {
		const ids = new Set<string>();
		basePreviewComponents.forEach((component) => {
			if (component.componentType !== ComponentTypes.LatestArticleGrid) return;
			const content = (component.content ?? {}) as LatestArticleGridContent;
			if (content.mode !== "dynamic") return;
			const id = (content.dynamicComponent?.dynamicPageId ?? "").toString().trim();
			if (id) ids.add(id);
		});
		return Array.from(ids);
	}, [basePreviewComponents]);
	const dynamicPageTemplateIds = useMemo(
		() => Array.from(new Set([...newsSectionDynamicPageIds, ...paginatedActivityDynamicPageIds, ...latestArticleDynamicPageIds])),
		[newsSectionDynamicPageIds, paginatedActivityDynamicPageIds, latestArticleDynamicPageIds]
	);

	// --- Custom hooks ---
	const {
		stringifyVariableValue,
		parseDynamicDate,
		formatDynamicDate,
		formatVariableValue
	} = useVariableFormatting();

	const {
		buildBreadcrumbsFromCategoryPathIds,
		buildBreadcrumbsFromKeyPath,
		applyArticleHeroBreadcrumbs
	} = useBreadcrumbs(categories, categoryKeyMap, categoryTitleMap);

	const {
		resolveNewsSectionDynamicContent,
		resolvePaginatedActivityGridDynamicContent,
		resolveLatestArticleGridDynamicContent,
		resolveMarketsBoardDynamicContent,
		buildDynamicPreviewComponents
	} = useDynamicContentResolvers({
		taxonomyMap,
		categoryKeyMap,
		categoryTitleMap,
		contentTypeConfigs,
		draftLanguage: draft?.language,
		newsSectionItemsByType,
		newsSectionDynamicPages,
		paginatedActivityItemsByType,
		latestArticleItemsByType,
		marketsBoardInflationItemsByType,
		formatVariableValue,
		stringifyVariableValue,
		parseDynamicDate,
		formatDynamicDate,
		applyArticleHeroBreadcrumbs
	});

	const persistence = usePagePersistence({
		routeId,
		routeSlug,
		slugFromQuery,
		prefix,
		initialLanguage,
		lang,
		slug,
		id,
		isNewDocument,
		reloadCount,
		setDraftWithComponents,
		draft
	});

	const {
		view, setView,
		loadingView,
		saving,
		error, setError,
		message, setMessage,
		deleting,
		restoring,
		isEditing, setIsEditing,
		loadPageView,
		persist,
		deployPreview,
		deployingPreview,
		performDelete,
		handleRestore,
		handleDelete,
		showDeleteConfirm, setShowDeleteConfirm
	} = persistence;

	// --- Preview components ---
	const previewComponents = useMemo<All8PageContent>(() => {
		const base =
			draft?.dynamicContentTypeId && dynamicPreviewSelection
				? buildDynamicPreviewComponents(basePreviewComponents, dynamicPreviewSelection, dynamicContentTypeConfig)
				: basePreviewComponents;
		return base.map((rawComponent) => {
			let component = rawComponent;
			if (component.componentType === ComponentTypes.NewsSection) {
				component = { ...component, content: resolveNewsSectionDynamicContent(component) };
			} else if (component.componentType === ComponentTypes.PaginatedActivityGrid) {
				component = { ...component, content: resolvePaginatedActivityGridDynamicContent(component) };
			} else if (component.componentType === ComponentTypes.LatestArticleGrid) {
				component = { ...component, content: resolveLatestArticleGridDynamicContent(component) };
			} else if (component.componentType === ComponentTypes.MarketsBoard) {
				component = { ...component, content: resolveMarketsBoardDynamicContent(component) };
			}
			// Overlay the baked block-fragments LAST so every computed dashboard widget renders its live
			// CDN data exactly as it will deploy: MarketsBoard cards (cours de change / inflation / taux
			// d'intérêt), the FxRatesExplorer converter, the InformationHub and the governors roster.
			// Non-widget components pass through untouched.
			return overlayBlockFragments(component, blockFragments);
		});
	}, [
		draft?.dynamicContentTypeId,
		dynamicPreviewSelection,
		basePreviewComponents,
		buildDynamicPreviewComponents,
		dynamicContentTypeConfig,
		resolveNewsSectionDynamicContent,
		resolvePaginatedActivityGridDynamicContent,
		resolveLatestArticleGridDynamicContent,
		resolveMarketsBoardDynamicContent,
		blockFragments
	]);

	const dynamicPreviewLabel = useMemo(() => {
		if (!dynamicPreviewSelection) return null;
		const data = dynamicPreviewSelection.data as { title?: string; slug?: string } | undefined;
		return data?.title ?? data?.slug ?? dynamicPreviewSelection.id ?? "Content item";
	}, [dynamicPreviewSelection]);

	const filteredDynamicPreviewItems = useMemo(() => {
		const term = dynamicPreviewSearch.trim().toLowerCase();
		if (!term) return dynamicPreviewItems;
		return dynamicPreviewItems.filter((item) => {
			const data = (item.data ?? {}) as { title?: string; slug?: string };
			const title = (data.title ?? "").toString().toLowerCase();
			const slugValue = (data.slug ?? "").toString().toLowerCase();
			const idValue = (item.id ?? "").toString().toLowerCase();
			return title.includes(term) || slugValue.includes(term) || idValue.includes(term);
		});
	}, [dynamicPreviewItems, dynamicPreviewSearch]);
	const allowDynamicComponents = !!draft?.dynamicContentTypeId;
	const availableLibrary = useMemo(
		() =>
			allowDynamicComponents
				? componentLibrary
				: componentLibrary.filter((definition) => definition.type !== ComponentTypes.DynamicComponents),
		[allowDynamicComponents]
	);
	const normalizeTag = useCallback((value?: string | null) => (value ?? "").trim().toLowerCase(), []);
	const availableTags = useMemo(() => {
		const tags = new Set<string>();
		availableLibrary.forEach((definition) => {
			(definition.tags ?? []).forEach((tag) => {
				const normalized = normalizeTag(tag);
				if (normalized) tags.add(normalized);
			});
		});
		return Array.from(tags).sort((a, b) => a.localeCompare(b));
	}, [availableLibrary, normalizeTag]);
	const toggleTagFilter = useCallback((tag: string) => {
		const normalized = normalizeTag(tag);
		if (!normalized) return;
		setComponentTagFilters((prev) => {
			const next = new Set(prev);
			if (next.has(normalized)) {
				next.delete(normalized);
			} else {
				next.add(normalized);
			}
			return Array.from(next);
		});
	}, [normalizeTag]);
	const clearTagFilters = useCallback(() => setComponentTagFilters([]), []);

	useEffect(() => {
		setComponentTagFilters((prev) => prev.filter((tag) => availableTags.includes(tag)));
	}, [availableTags]);

	const resolvedPath = `/page/${draft?.id || "[id]"}`;
	const { previewPageUrl, productionPageUrl } = useMemo(() => {
		const basePreview = process.env.REACT_APP_PUBLIC_PREVIEW_URL ?? "";
		const baseProduction = process.env.REACT_APP_PUBLIC_PRODUCTION_URL ?? "";
		const slugPart = (draft?.slug ?? "").trim().replace(/^\/+|\/+$/g, "");
		const path = slugPart || "";
		const noTrail = (base: string) => base.replace(/\/$/, "");
		return {
			previewPageUrl: basePreview ? (path ? `${noTrail(basePreview)}/${path}` : noTrail(basePreview)) : "",
			productionPageUrl: baseProduction ? (path ? `${noTrail(baseProduction)}/${path}` : noTrail(baseProduction)) : ""
		};
	}, [draft?.slug]);
	const isJsonLocked = contentViewMode === "json" && !!pageJsonError;

	useEffect(() => {
		if (contentViewMode !== "json") {
			setPageJsonDraft(serializePagePayload(draft, initialLanguage));
			setPageJsonError(null);
		}
	}, [contentViewMode, draft, initialLanguage]);

	useEffect(() => {
		setDynamicPreviewSelection(null);
	}, [draft?.dynamicContentTypeId]);

	useEffect(() => {
		if (!showDynamicPreview || !draft?.dynamicContentTypeId) return;
		let active = true;
		(async () => {
			setDynamicPreviewLoading(true);
			setDynamicPreviewError(null);
			try {
				const response = await client.content.apiContentSchemaKeyGet(draft.dynamicContentTypeId);
				const language = (draft.language ?? DEFAULT_LANGUAGE).toLowerCase();
				const items = (response.data ?? []).filter(
					(item) => !item?.isDeleted && (!item?.language || item.language === language)
				);
				if (active) setDynamicPreviewItems(items);
			} catch (err: any) {
				if (active) setDynamicPreviewError(err?.response?.data ?? err?.message ?? "Failed to load content items.");
			} finally {
				if (active) setDynamicPreviewLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [showDynamicPreview, draft?.dynamicContentTypeId, draft?.language]);

	useEffect(() => {
		if (newsSectionContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			setNewsSectionLoading((prev) => ({ ...prev, [contentTypeKey]: true }));
			setNewsSectionErrors((prev) => ({ ...prev, [contentTypeKey]: "" }));
			try {
				const response = await client.content.apiContentSchemaKeyGet(contentTypeKey);
				const items = (response.data ?? []).filter(
					(item) => !item?.isDeleted && (!item?.language || item.language === language)
				);
				if (active) {
					setNewsSectionItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch (err: any) {
				if (active) {
					setNewsSectionErrors((prev) => ({
						...prev,
						[contentTypeKey]: err?.response?.data ?? err?.message ?? "Failed to load content items."
					}));
				}
			} finally {
				if (active) {
					setNewsSectionLoading((prev) => ({ ...prev, [contentTypeKey]: false }));
				}
			}
		};
		newsSectionContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [newsSectionContentTypes, draft?.language]);

	useEffect(() => {
		if (paginatedActivityContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			setPaginatedActivityLoading((prev) => ({ ...prev, [contentTypeKey]: true }));
			setPaginatedActivityErrors((prev) => ({ ...prev, [contentTypeKey]: "" }));
			try {
				const response = await client.content.apiContentSchemaKeyGet(contentTypeKey);
				const items = (response.data ?? []).filter(
					(item) => !item?.isDeleted && (!item?.language || item.language === language)
				);
				if (active) {
					setPaginatedActivityItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch (err: any) {
				if (active) {
					setPaginatedActivityErrors((prev) => ({
						...prev,
						[contentTypeKey]: err?.response?.data ?? err?.message ?? "Failed to load content items."
					}));
				}
			} finally {
				if (active) {
					setPaginatedActivityLoading((prev) => ({ ...prev, [contentTypeKey]: false }));
				}
			}
		};
		paginatedActivityContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [paginatedActivityContentTypes, draft?.language]);

	useEffect(() => {
		if (latestArticleContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			try {
				const response = await client.content.apiContentSchemaKeyGet(contentTypeKey);
				const items = (response.data ?? []).filter(
					(item) => !item?.isDeleted && (!item?.language || item.language === language)
				);
				if (active) {
					setLatestArticleItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch {
				if (active) {
					setLatestArticleItemsByType((prev) => ({ ...prev, [contentTypeKey]: [] }));
				}
			}
		};
		latestArticleContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [latestArticleContentTypes, draft?.language]);

	// Load items for any content type referenced by a MarketsBoard inflation
	// block in dynamic mode. Mirrors the latestArticle loader — single-batch
	// fetch per key, cached in component state, language-filtered client-side.
	useEffect(() => {
		if (marketsBoardInflationContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			try {
				const response = await client.content.apiContentSchemaKeyGet(contentTypeKey);
				const items = (response.data ?? []).filter(
					(item) => !item?.isDeleted && (!item?.language || item.language === language)
				);
				if (active) {
					setMarketsBoardInflationItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch {
				if (active) {
					setMarketsBoardInflationItemsByType((prev) => ({ ...prev, [contentTypeKey]: [] }));
				}
			}
		};
		marketsBoardInflationContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [marketsBoardInflationContentTypes, draft?.language]);

	// Load the baked block-fragments (preview env) so the page preview renders every computed dashboard
	// widget — MarketsBoard cards (cours de change / inflation / taux d'intérêt), the FxRatesExplorer
	// converter, the InformationHub and the governors roster — from the SAME CDN files the live site
	// reads. This is what makes the preview match deployment uniformly (no per-widget special case) and
	// auto-align: when a feed re-bakes a fragment, reopening the preview shows the new figures. Re-fetched
	// when the draft language changes; a fragment that isn't baked yet is simply absent (placeholder).
	useEffect(() => {
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		loadBlockFragments("preview", language)
			.then((map) => {
				if (active) setBlockFragments(map);
			})
			.catch(() => {
				if (active) setBlockFragments({});
			});
		return () => {
			active = false;
		};
	}, [draft?.language]);

	useEffect(() => {
		if (dynamicPageTemplateIds.length === 0) {
			setNewsSectionDynamicPages({});
			return;
		}
		let active = true;
		(async () => {
			try {
				const response = await client.content.apiContentSchemaKeyGet(PAGE_SCHEMA_KEY);
				const docs: ContentDocument[] = response.data ?? [];
				const idSet = new Set(dynamicPageTemplateIds);
				const next: Record<string, ContentDocument> = {};
				docs.forEach((doc) => {
					if (!doc?.id || doc.isDeleted) return;
					if (!idSet.has(doc.id)) return;
					next[doc.id] = doc;
				});
				if (active) setNewsSectionDynamicPages(next);
			} catch {
				if (active) setNewsSectionDynamicPages({});
			}
		})();
		return () => {
			active = false;
		};
	}, [dynamicPageTemplateIds]);

	const loadDeploymentQueue = useCallback(async () => {
		if (!draft?.slug || !draft?.language) return;
		setDeploymentQueueLoading(true);
		try {
			const fetchQueue = async (target: "preview" | "production") => {
				const response = await client.deployment.apiDeploymentDeploymentQueueGet(target, {
					withCredentials: true,
					headers: { Accept: "application/json" }
				});
				return Array.isArray(response.data) ? response.data : [];
			};
			const [previewItems, productionItems] = await Promise.all([
				fetchQueue("preview"),
				fetchQueue("production")
			]);
			setDeploymentQueuePreview(previewItems);
			setDeploymentQueueProduction(productionItems);
		} catch (err: any) {
			console.error(err);
			setDeploymentQueuePreview([]);
			setDeploymentQueueProduction([]);
		} finally {
			setDeploymentQueueLoading(false);
		}
	}, [draft?.slug, draft?.language]);

	useEffect(() => {
		if (!draft?.slug || !draft?.language) return;
		loadDeploymentQueue();
	}, [draft?.slug, draft?.language, deploymentQueueRefresh, loadDeploymentQueue]);

	useEffect(() => {
		const pageKey = `${draft?.id ?? ""}-${draft?.slug ?? ""}-${draft?.language ?? ""}`;
		const enteringJson = contentViewMode === "json" && viewModeRef.current !== "json";
		const pageChangedWhileJson = contentViewMode === "json" && pageKey !== lastJsonPageKeyRef.current;
		if (contentViewMode === "json" && (enteringJson || pageChangedWhileJson)) {
			setPageJsonDraft(serializePagePayload(draft, initialLanguage));
			setPageJsonError(null);
			lastJsonPageKeyRef.current = pageKey;
		}
		if (contentViewMode !== "markdown") {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			if (viewModeRef.current === "markdown") {
				try {
					const latestMd = markdownDraftRef.current;
					const nextDraft = editableMarkdownToPage(latestMd, draft, initialLanguage ?? "fr");
					setDraftWithComponents(nextDraft);
				} catch (_) {
					// ignore parse errors when switching away
				}
			}
		}
		const prevViewMode = viewModeRef.current;
		const enteringMarkdown = contentViewMode === "markdown" && prevViewMode !== "markdown";
		const pageChanged = contentViewMode === "markdown" && pageKey !== lastMarkdownPageKeyRef.current;
		if (contentViewMode === "markdown" && (enteringMarkdown || pageChanged)) {
			const latestDraft = draftRef.current ?? draft;
			const md = pageToEditableMarkdown(latestDraft, initialLanguage ?? "fr");
			markdownDraftRef.current = md;
			setMarkdownDraft(md);
			lastDraftFromMarkdownRef.current = latestDraft;
			lastMarkdownPageKeyRef.current = pageKey;
			setMarkdownError(null);
		}
		viewModeRef.current = contentViewMode;
		return () => {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
		};
	}, [contentViewMode, draft, initialLanguage, setDraftWithComponents]);

	const handleStartEdit = useCallback(() => {
		setIsEditing(true);
		setError(null);
		setMessage(null);
	}, [setIsEditing, setError, setMessage]);

	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setError(null);
		setMessage(null);
		setReloadCount((c) => c + 1);
	}, [setIsEditing, setError, setMessage]);

	const openSlugChange = useCallback(() => {
		if (!draft?.slug || !draft?.language) return;
		setSlugChangeValue(draft.slug);
		setSlugChangeError(null);
		setShowSlugChange(true);
	}, [draft?.slug, draft?.language]);

	const openDynamicPreview = useCallback(() => {
		if (!draft?.dynamicContentTypeId) return;
		setDynamicPreviewError(null);
		setDynamicPreviewSearch("");
		setShowDynamicPreview(true);
	}, [draft?.dynamicContentTypeId]);

	const canEdit = isEditing && !loadingView;

	const normalizedQueueSlug = useMemo(() => (draft?.slug ?? "").trim().toLowerCase(), [draft?.slug]);
	const normalizedQueueLanguage = useMemo(
		() => (draft?.language ?? initialLanguage).toLowerCase(),
		[draft?.language, initialLanguage]
	);
	const previewQueueMatches = useMemo(() => {
		if (!normalizedQueueSlug || !normalizedQueueLanguage) return [];
		return deploymentQueuePreview.filter((item) => {
			const itemType = (item.contentType ?? "").toLowerCase();
			if (itemType !== PAGE_SCHEMA_KEY) return false;
			const itemLang = (item.language ?? "").toLowerCase();
			if (itemLang !== normalizedQueueLanguage) return false;
			const itemSlug = (item.slug ?? "").toLowerCase();
			if (itemSlug === normalizedQueueSlug) return true;
			const pagePaths = Array.isArray(item.pagePaths) ? item.pagePaths : [];
			return pagePaths.some((path) => (path ?? "").toLowerCase() === normalizedQueueSlug);
		});
	}, [deploymentQueuePreview, normalizedQueueLanguage, normalizedQueueSlug]);
	const productionQueueMatches = useMemo(() => {
		if (!normalizedQueueSlug || !normalizedQueueLanguage) return [];
		return deploymentQueueProduction.filter((item) => {
			const itemType = (item.contentType ?? "").toLowerCase();
			if (itemType !== PAGE_SCHEMA_KEY) return false;
			const itemLang = (item.language ?? "").toLowerCase();
			if (itemLang !== normalizedQueueLanguage) return false;
			const itemSlug = (item.slug ?? "").toLowerCase();
			if (itemSlug === normalizedQueueSlug) return true;
			const pagePaths = Array.isArray(item.pagePaths) ? item.pagePaths : [];
			return pagePaths.some((path) => (path ?? "").toLowerCase() === normalizedQueueSlug);
		});
	}, [deploymentQueueProduction, normalizedQueueLanguage, normalizedQueueSlug]);

	const previewStatus = useMemo(() => {
		if (!view?.preview) return null;
		const cur = view.current as ContentDocument | undefined;
		return {
			deployedAt: cur?.previewDeployedAt ?? view.preview.deployedAt ?? null,
			exists: view.preview.exists ?? false
		};
	}, [view]);

	const productionStatus = useMemo(() => {
		if (!view?.production) return null;
		const cur = view.current as ContentDocument | undefined;
		return {
			deployedAt: cur?.productionDeployedAt ?? view.production.deployedAt ?? null,
			exists: view.production.exists ?? false
		};
	}, [view]);

	const previewDeploymentState: DeploymentState = (() => {
		if (deploymentQueueLoading) return "pending";
		if (previewQueueMatches.length > 0) return "needs-deploy";
		return "up-to-date";
	})();

	const productionDeploymentState: DeploymentState = (() => {
		if (previewDeploymentState !== "up-to-date") return "pending";
		if (deploymentQueueLoading) return "pending";
		if (productionQueueMatches.length > 0) return "needs-deploy";
		return "up-to-date";
	})();

	const pendingDeployMessage =
		previewDeploymentState === "needs-deploy"
			? "Some changes are waiting to be deployed to preview."
			: productionDeploymentState === "needs-deploy"
			? "Some changes are waiting to be promoted to production."
			: null;

	const lastSavedFingerprint = useMemo(() => {
		if (!view?.current) return null;
		const saved = buildDraftFromContentDoc(
			view.current as ContentDocument,
			initialLanguage,
			DEFAULT_LANGUAGE
		);
		return saved ? previewFingerprint(saved, initialLanguage) : null;
	}, [view?.current, initialLanguage]);

	const hasUnsavedChanges =
		!!lastSavedFingerprint &&
		previewFingerprint(draft, initialLanguage) !== lastSavedFingerprint;

	const unsavedChangesMessage = hasUnsavedChanges ? "You have unsaved changes." : null;

	const saveBlockerReasons = useMemo(() => {
		if (saving || loadingView) return [];
		const reasons: string[] = [];
		if (!(draft?.slug ?? "").trim()) reasons.push("Slug is missing.");
		if (!(draft?.language ?? "").trim()) reasons.push("Language is required.");
		if (isJsonLocked) reasons.push("Fix JSON errors to save.");
		return reasons;
	}, [draft?.slug, draft?.language, isJsonLocked, saving, loadingView]);

	const openDisabled = !draft?.id;
	const connectionsDisabled = !draft?.slug || !draft?.language;
	const saveDisabled = !canEdit || saving || loadingView || isJsonLocked || !draft?.slug || !draft?.language;
	const deployPreviewDisabled = !canEdit || deployingPreview || saving || loadingView || !draft?.slug || !draft?.language;
	const deleteDisabled = deleting || !draft?.id || !draft?.slug || !draft?.language;
	const canChangeSlug = canEdit && !!draft?.id;
	const allowImageVariables = !!draft?.dynamicContentTypeId;

	const handleConfirmSlugChange = useCallback(async () => {
		if (!draft?.slug || !draft?.language) return;
		const nextSlug = slugChangeValue.trim();
		if (!nextSlug) {
			setSlugChangeError("New slug is required.");
			return;
		}
		setChangingSlug(true);
		setSlugChangeError(null);
		try {
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const response = await client.content.apiContentPageChangeSlugPost(
				{
					language: langCode,
					currentSlug: draft.slug,
					newSlug: nextSlug
				}
			);
			const vm = response.data ?? null;
			setView(vm);
			const nextDraft = buildDraftFromContentDoc(vm?.current as ContentDocument | undefined, langCode, DEFAULT_LANGUAGE);
			if (nextDraft) {
				setDraftWithComponents(nextDraft);
			}
			setShowSlugChange(false);
			showSuccessModal({
				title: "Slug updated",
				message: "Page slug updated and references have been refreshed."
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? "Slug change failed.";
			setSlugChangeError(String(apiMessage));
		} finally {
			setChangingSlug(false);
		}
	}, [draft?.slug, draft?.language, slugChangeValue, initialLanguage, setDraftWithComponents, setView]);


	const filteredLibrary = useMemo(() => {
		const term = componentSearch.trim().toLowerCase();
		return availableLibrary.filter((definition) => {
			const matchesTerm =
				!term ||
				definition.title.toLowerCase().includes(term) ||
				definition.description.toLowerCase().includes(term) ||
				(definition.tags ?? []).some((tag) => tag.toLowerCase().includes(term));
			const definitionTags = (definition.tags ?? []).map(normalizeTag);
			const matchesTags =
				componentTagFilters.length === 0 ||
				definitionTags.some((tag) => componentTagFilters.includes(tag));
			return matchesTerm && matchesTags;
		});
	}, [componentSearch, availableLibrary, componentTagFilters, normalizeTag]);

	useEffect(() => {
		const currentType = librarySelection?.type;
		if (currentType && availableLibrary.some((definition) => definition.type === currentType)) return;
		setLibrarySelection(availableLibrary[0] ?? null);
	}, [availableLibrary, librarySelection]);

	useEffect(() => {
		if (libraryView === "visual") {
			const existingType = librarySelection?.type;
			const stillValid = existingType && filteredLibrary.some((def) => def.type === existingType);
			if (!stillValid) {
				setLibrarySelection(filteredLibrary[0] ?? null);
			}
		}
	}, [libraryView, filteredLibrary, librarySelection]);

	const runtimeImageComponent = useMemo(() => createRuntimeImageComponent(), []);
	const runtimeConfig = useMemo(
		() => ({
			disableLinks: true,
			imageComponent: runtimeImageComponent
		}),
		[runtimeImageComponent]
	);
	const previewWidth = PREVIEW_VIEWPORTS[previewViewport];

	useEffect(() => {
		const el = previewContainerRef.current;
		if (!el) return;
		const updateWidth = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setPreviewContainerWidth(w);
		};
		updateWidth();
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			const w = entry?.contentRect?.width ?? 0;
			if (w > 0) setPreviewContainerWidth(w);
		});
		observer.observe(el);
		const raf = requestAnimationFrame(() => {
			requestAnimationFrame(updateWidth);
		});
		return () => {
			observer.disconnect();
			cancelAnimationFrame(raf);
		};
	}, [previewViewport]);

	const previewCss = `
    .page-editor-preview {
      max-width: ${PREVIEW_MAX_WIDTH}px;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    }
    .page-editor-preview section {
      margin-left: 0 !important;
      margin-right: 0 !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
    }
    .page-editor-preview section[class*="mx-[calc(50%-50vw)"] {
      margin-left: auto !important;
      margin-right: auto !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
      width: 100% !important;
    }
    .page-editor-preview section .max-w-7xl {
      max-width: ${PREVIEW_MAX_WIDTH}px;
    }
  `;

	const jsonEditorOptions = useMemo(
		() => ({
			minimap: { enabled: false },
			lineNumbers: "on" as const,
			folding: true,
			showFoldingControls: "always" as const,
			formatOnPaste: true,
			formatOnType: true,
			wordWrap: "on" as const,
			scrollBeyondLastLine: false,
			automaticLayout: true,
			tabSize: 2
		}),
		[]
	);

	const updateComponentValue = useCallback(
		(index: number, path: Path, value: any) => {
			if (!canEdit) return;
			setDraft((prev) => {
				if (!prev) return prev;
				const components = normalizeComponents(prev.components);
				if (!components[index]) return prev;
				let updatedComponent = setValueAtPath(components[index], path, value);
				if (updatedComponent.componentType === ComponentTypes.ArticleHero) {
					const content = (updatedComponent.content ?? {}) as Record<string, any>;
					const source = (content.breadcrumbsSource ?? "manual").toString();
					if (source === "categories") {
						const keyPath = (content.breadcrumbsCategoryPath ?? "").toString().trim();
						const breadcrumbs = keyPath
							? buildBreadcrumbsFromKeyPath(keyPath, content.breadcrumbsLink)
							: buildBreadcrumbsFromCategoryPathIds(
									Array.isArray(content.breadcrumbsCategoryPathIds)
										? content.breadcrumbsCategoryPathIds.map(String)
										: [],
									content.breadcrumbsLink
							  );
						updatedComponent = {
							...updatedComponent,
							content: {
								...content,
								breadcrumbs: breadcrumbs ?? []
							}
						};
					} else if (source === "dynamic") {
						updatedComponent = {
							...updatedComponent,
							content: { ...content, breadcrumbs: [] }
						};
					}
				}
				const nextComponents = [...components];
				nextComponents[index] = updatedComponent;
				return { ...prev, components: nextComponents };
			});
		},
		[buildBreadcrumbsFromCategoryPathIds, buildBreadcrumbsFromKeyPath, canEdit, setDraft]
	);

	const handlePageJsonChange = useCallback(
		(nextValue: string) => {
			if (!canEdit) return;
			setPageJsonDraft(nextValue);
			try {
				const nextDraft = parsePagePayload(nextValue, draft, initialLanguage);
				setDraftWithComponents(nextDraft);
				setPageJsonError(null);
			} catch {
				setPageJsonError("JSON not valid.");
			}
		},
		[canEdit, draft, initialLanguage, setDraftWithComponents]
	);

	const handleFormatJson = useCallback(() => {
		try {
			const formatted = JSON.stringify(JSON.parse(pageJsonDraft), null, 2);
			handlePageJsonChange(formatted);
		} catch {
			setPageJsonError("JSON not valid.");
		}
	}, [pageJsonDraft, handlePageJsonChange]);

	const handleMarkdownChange = useCallback(
		(nextValue: string) => {
			if (!canEdit) return;
			markdownDraftRef.current = nextValue;
			setMarkdownDraft(nextValue);
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			markdownPreviewDebounceRef.current = setTimeout(() => {
				markdownPreviewDebounceRef.current = null;
				try {
					const nextDraft = editableMarkdownToPage(nextValue, draft, initialLanguage ?? "fr");
					setDraftWithComponents(nextDraft);
					lastDraftFromMarkdownRef.current = nextDraft;
					setMarkdownError(null);
				} catch (err) {
					setMarkdownError(err instanceof Error ? err.message : "Markdown invalid.");
				}
			}, MARKDOWN_PREVIEW_DEBOUNCE_MS);
		},
		[canEdit, draft, initialLanguage, setDraftWithComponents]
	);

	const removeComponent = useCallback((index: number) => {
		if (!canEdit) return;
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const nextComponents = components.filter((_, i) => i !== index);
			return { ...prev, components: nextComponents };
		});
	}, [canEdit, setDraft]);

	const moveComponent = useCallback((from: number, to: number) => {
		if (!canEdit) return;
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const nextComponents = reorderList(components, from, to);
			return { ...prev, components: nextComponents };
		});
	}, [canEdit, setDraft]);

	const markdownInsertRef = useRef<((def: ComponentDefinition) => void) | null>(null);

	const addComponentFromDefinition = useCallback((definition: ComponentDefinition | null) => {
		if (!definition || !canEdit) return;
		if (contentViewMode === "markdown" && markdownInsertRef.current) {
			markdownInsertRef.current(definition);
			setShowLibrary(false);
			return;
		}
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const nextComponent = createComponentInstance(definition);
			return { ...prev, components: [...components, nextComponent] };
		});
		setShowLibrary(false);
	}, [canEdit, contentViewMode, setDraft, setShowLibrary]);

	const pageHeaderTitle = useMemo(() => {
		const t = draft?.title?.trim();
		if (t) return t;
		const components = draft?.components ? normalizeComponents(draft.components) : [];
		const first = components[0];
		if (first?.componentType === ComponentTypes.PageHeader && first?.content && typeof first.content === "object" && first.content !== null) {
			const headerTitle = String((first.content as { title?: string }).title ?? "").trim();
			if (headerTitle) return headerTitle;
		}
		return draft?.slug?.trim() || (slug ? "Edit page" : "New page");
	}, [draft?.title, draft?.slug, draft?.components, slug]);

	usePageHeader({
		title: pageHeaderTitle,
		actions: (
			<PageEditorHeaderActions
				isEditing={isEditing}
				onStartEdit={handleStartEdit}
				onCancelEdit={handleCancelEdit}
				onOpenExternal={() => {
					if (!draft?.id) return;
					const targetUrl = `${DEFAULT_DOMAIN.replace(/\/+$/, "")}/page/${draft.id}`;
					window.open(targetUrl, "_blank");
				}}
				openDisabled={openDisabled}
				onSave={persist}
				saveDisabled={saveDisabled}
				saving={saving}
				loading={loadingView}
				onDelete={handleDelete}
				deleteDisabled={deleteDisabled}
				deleting={deleting}
				onAnalyze={() => setShowPageAnalyze(true)}
				onTranslate={() => {
					// The per-field translation workspace replaced the old whole-document modal.
					const params = new URLSearchParams();
					if (draft?.slug) params.set("slug", draft.slug);
					params.set("lang", "en");
					history.push(`/traduction/page?${params.toString()}`);
				}}
			/>
		)
	});

	return (
		<>
			{/* Soft edit-lock contention warning — only renders when another admin holds this page. */}
			<EditLockBanner contentId={draft?.id} className="mx-4 mt-3 sm:mx-6" />
			<PageEditorModals
				showDeleteConfirm={showDeleteConfirm}
				onHideDeleteConfirm={() => setShowDeleteConfirm(false)}
				onConfirmDelete={() => performDelete()}
				deleting={deleting}
				showSlugChange={showSlugChange}
				onHideSlugChange={() => setShowSlugChange(false)}
				slugChangeValue={slugChangeValue}
				onSlugChangeValueChange={setSlugChangeValue}
				slugChangeError={slugChangeError}
				changingSlug={changingSlug}
				onConfirmSlugChange={() => handleConfirmSlugChange()}
				showDynamicPreview={showDynamicPreview}
				onHideDynamicPreview={() => setShowDynamicPreview(false)}
				dynamicPreviewSearch={dynamicPreviewSearch}
				onDynamicPreviewSearchChange={setDynamicPreviewSearch}
				dynamicPreviewError={dynamicPreviewError}
				dynamicPreviewLoading={dynamicPreviewLoading}
				filteredDynamicPreviewItems={filteredDynamicPreviewItems}
				onSelectDynamicPreviewItem={(item) => {
					setDynamicPreviewSelection(item);
					setShowDynamicPreview(false);
				}}
			/>
			<PageEditorLayout
			editable={canEdit}
			error={error}
			message={message}
			unsavedChangesMessage={unsavedChangesMessage}
			saveBlockerReasons={saveBlockerReasons}
			pendingDeployMessage={pendingDeployMessage}
			loadingView={loadingView}
			draft={draft}
			contentViewMode={contentViewMode}
			metaOpen={metaOpen}
			setMetaOpen={setMetaOpen}
			setDraft={setDraft}
			resolvedPath={resolvedPath}
			keywordsToInput={keywordsToInput}
			inputToKeywords={inputToKeywords}
			canChangeSlug={canChangeSlug}
			onOpenSlugChange={openSlugChange}
			dynamicPreviewLabel={dynamicPreviewLabel}
			onOpenDynamicPreview={draft?.dynamicContentTypeId ? openDynamicPreview : undefined}
			onClearDynamicPreview={() => setDynamicPreviewSelection(null)}
			previewViewport={previewViewport}
			setPreviewViewport={setPreviewViewport}
			previewPageUrl={previewPageUrl}
			productionPageUrl={productionPageUrl}
			previewContainerRef={previewContainerRef}
			previewCss={previewCss}
			editorComponents={basePreviewComponents}
			previewComponents={previewComponents}
			previewWidth={previewWidth}
			previewContainerWidth={previewContainerWidth}
			runtimeConfig={runtimeConfig}
			allowImageVariables={allowImageVariables}
			activeComponentKey={activeComponentKey}
			setActiveComponentKey={setActiveComponentKey}
			updateComponentValue={updateComponentValue}
			removeComponent={removeComponent}
			moveComponent={moveComponent}
			jsonDrafts={jsonDrafts}
			setJsonDrafts={setJsonDrafts}
			jsonErrors={jsonErrors}
			setJsonErrors={setJsonErrors}
			showLibrary={showLibrary}
			setShowLibrary={setShowLibrary}
			libraryView={libraryView}
			setLibraryView={setLibraryView}
			componentSearch={componentSearch}
			setComponentSearch={setComponentSearch}
			filteredLibrary={filteredLibrary}
			availableTags={availableTags}
			activeTags={componentTagFilters}
			onToggleTag={toggleTagFilter}
			onClearTags={clearTagFilters}
			componentDefinitionMap={componentDefinitionMap}
			librarySelection={librarySelection}
			setLibrarySelection={setLibrarySelection}
			addComponentFromDefinition={addComponentFromDefinition}
			markdownInsertRef={markdownInsertRef}
			pageJsonDraft={pageJsonDraft}
			pageJsonError={pageJsonError}
			handleFormatJson={handleFormatJson}
			handlePageJsonChange={handlePageJsonChange}
			jsonEditorOptions={jsonEditorOptions}
			isJsonLocked={isJsonLocked}
			onSelectBlocks={() => setContentViewMode("blocks")}
			onSelectJson={() => setContentViewMode("json")}
			onSelectMarkdown={() => {
				const latestDraft = draftRef.current ?? draft;
				const md = pageToEditableMarkdown(latestDraft, initialLanguage ?? "fr");
				markdownDraftRef.current = md;
				setMarkdownDraft(md);
				setMarkdownError(null);
				setContentViewMode("markdown");
			}}
			markdownDraft={markdownDraft}
			markdownError={markdownError}
			onMarkdownChange={handleMarkdownChange}
			onShowConnections={() => setConnectionsOpen(true)}
			connectionsDisabled={connectionsDisabled}
			onDeployPreview={deployPreview}
			deployPreviewDisabled={deployPreviewDisabled}
			deployingPreview={deployingPreview}
			previewState={previewDeploymentState}
			previewDeployedAt={previewStatus?.deployedAt ?? null}
			productionState={productionDeploymentState}
			productionDeployedAt={productionStatus?.deployedAt ?? null}
			onGoToDeployment={() => history.push("/deployment")}
			onSelectPageFromTree={(node) => {
				const nodeId = node?.id ?? null;
				const nodeSlug = node?.slug ?? node?.path ?? null;
				const nodeLanguage = (node?.language ?? draft?.language ?? initialLanguage ?? DEFAULT_LANGUAGE).toString();
				if (nodeId) {
					history.push(`/page/${encodeURIComponent(nodeId)}`);
					return;
				}
				if (nodeSlug) {
					history.push(`/page/${encodeURIComponent(nodeLanguage)}/${nodeSlug}`);
				}
			}}
			connectionsOpen={connectionsOpen}
			setConnectionsOpen={setConnectionsOpen}
			initialLanguage={initialLanguage}
			pageNavOpen={pageNavOpen}
			setPageNavOpen={setPageNavOpen}
			isPageDeleted={draft?.isDeleted === true}
			onRestore={handleRestore}
			restoring={restoring}
		/>
		<AIAnalyzeModal
			show={showPageAnalyze}
			onClose={() => setShowPageAnalyze(false)}
			schemaKey="page"
			language={draft?.language ?? "fr"}
			contentId={draft?.id ?? undefined}
			slug={draft?.slug ?? undefined}
			data={{
				title: draft?.title ?? "",
				slug: draft?.slug ?? "",
				metadata: draft?.metadata ?? {},
				components: draft?.components ?? []
			}}
		/>
		</>
	);
};

export default PageEditor;
